import {
  LineChart,
  LoaderTalos,
  renderToHTML,
  toBigWithDefault,
  useAssetsContext,
  useDynamicCallback,
  useHighchartsRef,
  useObservableValue,
  wsScanToMap,
  type LedgerEvent,
  type ReconMismatch,
  type SubscriptionResponse,
} from '@talos/kyoko';
import Big from 'big.js';
import type * as Highcharts from 'highcharts/highcharts';
import { compact } from 'lodash-es';
import { transparentize } from 'polished';
import { useEffect, useMemo } from 'react';
import { map, type Observable } from 'rxjs';
import { useTheme, type DefaultTheme } from 'styled-components';
import { LedgerEventTooltip } from './LedgerEventTooltip';
import { useReconDetailsInteractions } from './ReconDetailsInteractionsProvider';

interface LedgerEventsScatterChartProps {
  reconMismatch: ReconMismatch;
  ledgerEventsObs: Observable<SubscriptionResponse<LedgerEvent>>;
}

export const LedgerEventsScatterChart = ({ reconMismatch, ledgerEventsObs }: LedgerEventsScatterChartProps) => {
  const theme = useTheme();
  const { resolveSecurityOrCurrency } = useAssetsContext();
  const { chartRef, isLoaded, setChartObject } = useHighchartsRef();

  const { setZoomOutChart, highlightRows } = useReconDetailsInteractions();
  // Register chart zoom out with the interactions provider
  useEffect(() => {
    if (isLoaded) {
      const zoomOut = () => chartRef.current?.zoomOut();
      setZoomOutChart(() => zoomOut);
    }
  }, [chartRef, isLoaded, setZoomOutChart]);

  const ledgerEvents = useObservableValue(
    () =>
      ledgerEventsObs.pipe(
        wsScanToMap({ getUniqueKey: le => le.rowID, newMapEachUpdate: false }),
        map(map => [...map.values()])
      ),
    [ledgerEventsObs]
  );

  const getTooltip = useDynamicCallback(
    (data: Highcharts.TooltipFormatterContextObject, tooltip: Highcharts.Tooltip) => {
      // Highcharts types the custom record as a Dictionary without ability to type the keys to be specific, so do this instead
      const customStruct = data.point?.options.custom as DataPointCustomStruct | undefined;
      // Be a bit safe here
      const ledgerEvents = customStruct?.data instanceof Array ? customStruct.data : undefined;

      if (!ledgerEvents || ledgerEvents.length === 0) {
        return '';
      }

      const { security, currency } = resolveSecurityOrCurrency(ledgerEvents[0].Asset);
      const assetInfo = security ?? currency;
      if (!assetInfo) {
        return '';
      }

      return renderToHTML(<LedgerEventTooltip ledgerEvents={ledgerEvents} assetInfo={assetInfo} />, theme);
    }
  );

  const pointClicked = useDynamicCallback((event: Highcharts.PointClickEventObject) => {
    if (!highlightRows) {
      return;
    }

    const customStruct = event.point.options.custom as DataPointCustomStruct | undefined;
    if (!customStruct) {
      return;
    }

    highlightRows(customStruct.data);
  });

  const options = useMemo(() => {
    if (!ledgerEvents) {
      return undefined;
    }
    const ledgerEventsByTransactTime = new Map<string, LedgerEvent[]>();
    for (const ledgerEvent of ledgerEvents) {
      const arr = ledgerEventsByTransactTime.get(ledgerEvent.TransactTime) ?? [];
      arr.push(ledgerEvent);
      ledgerEventsByTransactTime.set(ledgerEvent.TransactTime, arr);
    }
    const datapoints = compact(
      [...ledgerEventsByTransactTime.values()].map(ledgerEvents => getDataPoint(ledgerEvents, theme))
    );

    return {
      chart: {
        // the chart isnt really a scatter chart, its actually a line chart... but it looks like a scatter chart :)
        // we make it a fake-scatter-but-actually-a-line-chart so that we can use the highcharts stock mousewheel zooming
        type: 'line',
        marginTop: 1, // 1 pixel needed to show the border
        marginBottom: 1, // manually found number that fits the bottom labels nicely
        marginRight: 1,
        marginLeft: 1,

        animation: false,
        zooming: {
          mouseWheel: {
            enabled: true,
          },
        },
      },
      tooltip: {
        useHTML: true,
        outside: true,
        formatter: function (tooltip: Highcharts.Tooltip) {
          return getTooltip(this, tooltip);
        },
      },

      legend: {
        enabled: false,
      },
      navigator: {
        enabled: false,
      },
      series: [
        {
          type: 'line',
          data: datapoints,
          states: {
            hover: {
              halo: {
                size: 0,
                opacity: 0,
              },
            },
          },
          point: {
            events: {
              click: pointClicked,
            },
          },
          animation: false,
          color: 'transparent',
          marker: {
            symbol: 'diamond',
            radius: 6,
            states: {
              hover: {
                enabled: true,
                radius: 8,
                lineWidth: 1,
              },
              select: {
                enabled: false,
              },
            },
          },
        },
      ],
      xAxis: {
        ordinal: false,
        crosshair: false,
        type: 'datetime',
        min: reconMismatch.bufferedStartTime.getTime(),
        max: reconMismatch.bufferedEndTime.getTime(),
        minorGridLineWidth: 1,
        minorGridLineColor: theme.borderColorChartAxis,
        plotLines: [
          {
            value: new Date(reconMismatch.StartTime).getTime(),
          },
          { value: new Date(reconMismatch.EndTime).getTime() },
        ],
      },
      yAxis: {
        labels: {
          enabled: false,
        },
        min: 0,
        max: 2,
        tickLength: 0,
        endOnTick: true,
        plotBands: [
          {
            from: 0,
            to: 2,
            color: 'rgba(229,229,230,0.02)',
          },
        ],
      },
    } satisfies Highcharts.Options;
  }, [ledgerEvents, reconMismatch, theme, getTooltip, pointClicked]);

  if (options == null) {
    return <LoaderTalos />;
  }

  return <LineChart options={options} showResetZoomButton={false} onChartCreated={setChartObject} />;
};

interface DataPointCustomStruct {
  data: LedgerEvent[];
}

function getDataPoint(ledgerEvents: LedgerEvent[], theme: DefaultTheme): Highcharts.PointOptionsObject | undefined {
  if (ledgerEvents.length === 0) {
    return undefined;
  }
  const firstLE = ledgerEvents[0];

  const totalDeltaAmountBig = ledgerEvents.reduce(
    (acc, ledgerEvent) => acc.plus(toBigWithDefault(ledgerEvent.Amount, 0)),
    Big(0)
  );

  const color = totalDeltaAmountBig.eq(0)
    ? theme.colorTextAttention
    : totalDeltaAmountBig.gt(0)
      ? theme.colors.green.lighten
      : theme.colors.red.lighten;

  // essentially just applying an opacity of 0.5
  const transparentizedColor = transparentize(0.5)(color);

  return {
    x: new Date(firstLE.TransactTime).getTime(),
    y: 1,
    custom: {
      data: ledgerEvents, // attach the list of ledger events this data point represents so we can detail them in the tooltip
    } satisfies DataPointCustomStruct,
    marker: { lineColor: color, lineWidth: 1, fillColor: transparentizedColor },
  };
}
